import "./service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

const PRODUCT_URL = "https://msghub.bestcodetechnology.in";

/** The three delivery channels a single send can fan out to. */
const channels = [
  {
    icon: "chat",
    title: "WhatsApp",
    desc: "Send through your own WhatsApp Business account with rich media and attachments — reaching people where they already read every message.",
  },
  {
    icon: "document",
    title: "Email",
    desc: "Send branded messages with attachments through your connected Gmail account — the same template, formatted for the inbox.",
  },
  {
    icon: "mobile",
    title: "SMS",
    desc: "Short, direct, and almost always read within minutes — the channel to reach for when a message can't wait.",
  },
];

const offerings = [
  {
    icon: "users",
    title: "Contacts & Segments",
    desc: "Import contacts in bulk, tag them by category, and let automatic dedupe catch duplicates. Build segments once and reuse them across every send.",
  },
  {
    icon: "document",
    title: "Visual Templates",
    desc: "Design a message once with variables like {{name}}, and see a live preview of exactly how it renders on WhatsApp, Email, and SMS before you send it.",
  },
  {
    icon: "rocket",
    title: "Bulk Send",
    desc: "Pick a template, pick a segment, and send to thousands of contacts in one job — with a live progress bar so you always know where the send stands.",
  },
  {
    icon: "activity",
    title: "Delivery History",
    desc: "Every message is logged per recipient with its real status — sent, delivered, or failed — so you know exactly what landed and what needs a retry.",
  },
  {
    icon: "shield",
    title: "Team Roles",
    desc: "Owners and admins get full access; members are scoped to contacts, templates, and sending. Invite teammates by email and control who can do what.",
  },
  {
    icon: "plug",
    title: "Provider Webhooks",
    desc: "Connect your WhatsApp Business, Gmail, and SMS provider credentials — messages send through provider accounts you already control, not a shared pool.",
  },
];

/** The two AI capabilities, called out separately from the core feature set. */
const aiFeatures = [
  {
    icon: "sparkles",
    title: "AI Compose",
    desc: "Stuck on wording? Describe what you want to say — a shipping update, a reminder, a promo — and AI drafts the message for you. It lands right in the template editor, ready to tweak before you save or send, so you're always editing, never starting blank.",
  },
  {
    icon: "brain",
    title: "AI Smart Import",
    desc: "Have a contact list stuck in a PDF, a spreadsheet, or a wall of pasted text? Upload it and AI reads through it, pulls out every name, phone, and email it can find, and hands you an editable preview table — nothing is saved until you confirm it.",
  },
];

const steps = [
  { icon: "document", label: "1 template", desc: "Design a message once, with variables and media." },
  { icon: "users", label: "N contacts", desc: "Pick a segment — from 10 contacts to your whole list." },
  { icon: "workflow", label: "3 channels", desc: "WhatsApp, Email, and SMS — together or apart." },
  { icon: "check", label: "Delivered", desc: "Live progress, then per-recipient status in History." },
];

const techGroups = [
  {
    label: "Messaging Channels",
    items: ["WhatsApp Cloud API", "Gmail", "MSG91 SMS"],
  },
  {
    label: "Import Sources",
    items: ["CSV / Excel import", "PDF import", "Pasted text"],
  },
  {
    label: "Integrations",
    items: ["Outbound webhooks", "REST API"],
  },
];

const differentiators = [
  {
    icon: "layers",
    title: "One Inbox for Every Channel",
    desc: "No more switching between a WhatsApp Business console, an email tool, and an SMS gateway — contacts, templates, sends, and history all live in the same dashboard.",
  },
  {
    icon: "search",
    title: "See It Before You Send It",
    desc: "The template designer shows a live, channel-accurate preview as you type — no guessing how variables, media, or line breaks will actually render.",
  },
  {
    icon: "gauge",
    title: "Know What Actually Landed",
    desc: "Bulk sends aren't \"fire and forget\" — every recipient gets a tracked status (sent, delivered, or failed), so you can see exactly what needs a follow-up.",
  },
];

export default function MessageHub() {
  const [channelsRef, channelsVisible] = useReveal();
  const [stepsRef, stepsVisible] = useReveal();
  const [aiRef, aiVisible] = useReveal();
  const [offeringsRef, offeringsVisible] = useReveal();
  const [diffRef, diffVisible] = useReveal();

  return (
    <>
      <SEO
        title="MessageHub: Bulk WhatsApp, Email & SMS Platform | Best Code Technology"
        description="Design one message and send it over WhatsApp, Email, and SMS from a single dashboard. AI-assisted composing, contact import, templates, and per-recipient delivery tracking."
        keywords="bulk messaging, WhatsApp Business API, bulk SMS, email campaigns, multi-channel messaging, AI compose"
        url="https://bestcodetechnology.in/services/messagehub"
      />

      <div className="svc-page">
        {/* ── Hero ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <div className="svc-hero__eyebrow">
                  <span className="pill-label">
                    One template, three channels, thousands of contacts
                  </span>
                </div>
                <h1 className="svc-hero__headline">
                  Bulk Send That Reaches{" "}
                  <span>Every Inbox and Chat</span>
                </h1>
                <p className="svc-hero__desc">
                  Design one message, pick your contacts, and send it over
                  WhatsApp, Email, and SMS — together or apart — from a single
                  dashboard built for teams that send at scale.
                </p>
                <div className="svc-hero__actions">
                  <a
                    className="btn btn-primary-custom"
                    href={PRODUCT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Start free
                    <Icon name="arrowRight" size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Channels ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">Channels</span>
                <h2 className="svc-section-title">Every Channel, One Send</h2>
                <p className="svc-section-sub">
                  Compose once. MessageHub adapts formatting per channel and
                  delivers through the provider you already trust.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={channelsRef}>
              {channels.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-4 reveal ${
                    channelsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  How Bulk Send Works
                </span>
                <h2 className="svc-section-title">
                  One Send, Thousands Reached
                </h2>
                <p className="svc-section-sub">
                  No batching numbers by hand, no copy-pasting into three
                  different tools — one job covers the whole list.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={stepsRef}>
              {steps.map((step, i) => (
                <div
                  key={step.label}
                  className={`col-6 col-lg-3 reveal ${
                    stepsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={step.icon} size={22} />
                      </div>
                    </div>
                    <h4 className="svc-offer-card__title">{step.label}</h4>
                    <p className="svc-offer-card__desc">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── AI ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  Powered by AI
                </span>
                <h2 className="svc-section-title">Let AI Do the Busywork</h2>
                <p className="svc-section-sub">
                  Two real, built-in AI tools — not a gimmick bolted on top.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={aiRef}>
              {aiFeatures.map((feat, i) => (
                <div
                  key={feat.title}
                  className={`col-12 col-lg-6 reveal ${
                    aiVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__top">
                      <div className="svc-usecase-card__icon">
                        <Icon name={feat.icon} size={22} />
                      </div>
                    </div>
                    <h3 className="svc-usecase-card__title">{feat.title}</h3>
                    <p className="svc-usecase-card__desc">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Product features ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">Product</span>
                <h2 className="svc-section-title">
                  Everything You Need to Send at Scale
                </h2>
                <p className="svc-section-sub">
                  Contacts, templates, sending, and delivery tracking — managed
                  in one place by one team.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={offeringsRef}>
              {offerings.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${
                    offeringsVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Integrations ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row align-items-start g-5">
              <div className="col-12 col-lg-4 svc-tech__col-left pe-lg-5">
                <span className="pill-label svc-section-label">
                  Integrations
                </span>
                <h2 className="svc-section-title">
                  Connects to the Systems You Already Run On
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Messages send through your own provider accounts, so your
                  sender identity, limits, and history stay yours.
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="svc-tech__groups">
                  {techGroups.map((group) => (
                    <div key={group.label} className="svc-tech__group">
                      <span className="svc-tech__group-label">
                        {group.label}
                      </span>
                      <div className="svc-tech__badges">
                        {group.items.map((tech) => (
                          <span key={tech} className="svc-tech__badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Why teams switch ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  Why Teams Switch
                </span>
                <h2 className="svc-section-title">
                  Built for How Sending Actually Works
                </h2>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={diffRef}>
              {differentiators.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-lg-4 reveal ${
                    diffVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__top">
                      <div className="svc-usecase-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <h3 className="svc-usecase-card__title">{item.title}</h3>
                    <p className="svc-usecase-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="row justify-content-center mt-5">
              <div className="col-12 text-center">
                <a
                  className="btn btn-primary-custom"
                  href={PRODUCT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get started free
                  <Icon name="arrowRight" size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
